import React, { useEffect, useState } from 'react';
import { Download, FileText, ChevronRight, Lock, GraduationCap } from 'lucide-react';
import api from '../../services/api';
import {
  downloadBlob,
  filenameFromContentDisposition,
} from '../../utils/download';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

const fmtInr = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const MyPayslips = () => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [intern, setIntern] = useState({
    isIntern: false,
    internshipStatus: null,
    internshipEndDate: null,
    internshipDays: null,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/employee/payroll/payslips');
        setPayslips(res.data.payslips || []);
        setIntern({
          isIntern: res.data.isIntern === true,
          internshipStatus: res.data.internshipStatus ?? null,
          internshipEndDate: res.data.internshipEndDate ?? null,
          internshipDays: res.data.internshipDays ?? null,
        });
      } catch (e) {
        console.error(e);
        toast.error('Failed to load payslips');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    const loadDetail = async () => {
      setDetailLoading(true);
      try {
        const res = await api.get(`/employee/payroll/payslip/${selectedId}/summary`);
        if (!cancelled) setDetail(res.data);
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          toast.error('Could not load payslip details');
          setDetail(null);
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const download = async (id, month, year) => {
    try {
      const res = await api.get(`/employee/payroll/payslip/${id}/pdf-zan`, {
        responseType: 'blob',
      });
      const mm = String(month).padStart(2, '0');
      const yy = String(year).slice(-2);
      const fname = filenameFromContentDisposition(
        res,
        `PFS_${mm}${yy}_payslip.pdf`,
      );
      downloadBlob(new Blob([res.data], { type: 'application/pdf' }), fname);
    } catch (e) {
      toast.error('Could not download payslip');
    }
  };

  const selectRow = (p) => {
    setSelectedId(p._id === selectedId ? null : p._id);
  };

  if (loading) {
    return <LoadingSpinner size="lg" className="py-16" />;
  }

  const e = detail?.earnings || {};
  const d = detail?.deductions || {};
  const breakdown = detail?.dayBreakdownSummary || {};
  const snap = detail?.snapshot || {};
  // A payslip counts as an intern payslip if the employee was an intern when it was
  // generated (snapshot), even if HR has since confirmed them as full-time.
  const payslipWasIntern = detail?.isIntern === true || snap.isIntern === true;
  const earningsRows = [
    ['Basic', e.basic],
    ['HRA', e.hra],
    ['Special allowance', e.specialAllowance],
    ['LTA', e.lta],
    ['IT', e.itAllowance],
    ['Meal allowance', e.mealAllowance],
    ['Bonus', e.bonus],
    ['Arrears', e.arrears || 0],
  ].filter(([, v]) => Number(v) > 0);
  const dedRows = [
    ['Absent days deduction', detail?.absentDeductionAmount],
    ['PF (employee)', d.pfEmployee],
    ['Professional tax', d.professionalTax],
    ['ESI (employee)', d.esiEmployee],
    ['Income tax / TDS', d.incomeTax],
    ['Labour welfare fund', d.labourWelfareFund],
  ].filter(([, v]) => Number(v) > 0);

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="h-8 w-8 text-primary-600" />
          My payslips
          {intern.isIntern && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
              <GraduationCap className="h-3.5 w-3.5" /> Intern
            </span>
          )}
        </h1>
        <p className="text-gray-600 mt-1">
          View amounts from payslips issued by HR. You cannot change payroll here — contact HR for
          corrections.
        </p>
      </div>

      {intern.isIntern && (
        <div className="flex items-start gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
          <GraduationCap className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" />
          <div>
            <p className="font-semibold">Intern — professional tax waived</p>
            <p className="mt-0.5 text-indigo-700">
              You are on an internship
              {intern.internshipEndDate
                ? ` until ${new Date(intern.internshipEndDate).toLocaleDateString('en-IN')}`
                : ''}
              . The ₹200 professional tax is not deducted from your stipend.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
        <Lock className="h-4 w-4 text-slate-500 shrink-0" />
        <span>
          This page is read-only. Download the official PDF for your records.
        </span>
      </div>

      <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10" />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Paid units
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gross
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Net pay
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  PDF
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payslips.map((p) => {
                const active = selectedId === p._id;
                return (
                  <tr
                    key={p._id}
                    className={`cursor-pointer transition-colors ${active ? 'bg-primary-50/60' : 'hover:bg-gray-50'}`}
                    onClick={() => selectRow(p)}
                  >
                    <td className="px-4 py-3 text-slate-400">
                      <ChevronRight
                        className={`h-4 w-4 transition-transform ${active ? 'rotate-90 text-primary-600' : ''}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {new Date(0, p.month - 1).toLocaleString('default', { month: 'long' })} {p.year}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{p.paidUnits}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{fmtInr(p.grossEarnings)}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">{fmtInr(p.netPay)}</td>
                    <td className="px-4 py-3 text-right text-sm" onClick={(ev) => ev.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => download(p._id, p.month, p.year)}
                        className="inline-flex items-center text-primary-600 hover:text-primary-800 font-medium"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {payslips.length === 0 && (
          <p className="text-center text-gray-500 py-12 text-sm px-4">
            No payslips yet. HR will publish them after payroll is processed for each month.
          </p>
        )}
      </div>

      {selectedId && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Payslip details</h2>
              {detail && (
                <p className="text-sm text-gray-600 mt-1">
                  {new Date(0, detail.period.month - 1).toLocaleString('default', {
                    month: 'long',
                  })}{' '}
                  {detail.period.year}
                  {detail.generatedAt && (
                    <>
                      {' '}
                      · Generated{' '}
                      {new Date(detail.generatedAt).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </>
                  )}
                </p>
              )}
            </div>
            {detail && (
              <button
                type="button"
                onClick={() => download(detail.payslipId, detail.period.month, detail.period.year)}
                className="inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 shrink-0"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </button>
            )}
          </div>

          {detailLoading && (
            <div className="py-8 flex justify-center">
              <LoadingSpinner />
            </div>
          )}

          {!detailLoading && detail && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm border-b border-gray-100 pb-6">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Gross</p>
                  <p className="text-lg font-semibold text-gray-900 mt-0.5">
                    {fmtInr(detail.grossEarnings)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Net pay</p>
                  <p className="text-lg font-semibold text-primary-700 mt-0.5">
                    {fmtInr(detail.netPay)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Paid units</p>
                  <p className="text-lg font-semibold text-gray-900 mt-0.5">{detail.paidUnits}</p>
                </div>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <span className="font-semibold">Comp Off included in paid leave:</span>{' '}
                {breakdown.compOffPaidFull || 0} full day(s)
                {breakdown.compOffPaidHalf ? `, ${breakdown.compOffPaidHalf} half day(s)` : ''}
              </div>

              {(snap.name || snap.employeeId) && (
                <div className="rounded-lg bg-slate-50 border border-slate-100 px-4 py-3 text-sm text-slate-700">
                  <p className="font-medium text-slate-900">{snap.name}</p>
                  <p className="text-slate-600 mt-1">
                    {snap.employeeId && <span>ID: {snap.employeeId}</span>}
                    {snap.department && (
                      <span className="ml-2">· {snap.department}</span>
                    )}
                    {snap.workLocation && (
                      <span className="ml-2">· {snap.workLocation}</span>
                    )}
                    {payslipWasIntern && (
                      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                        <GraduationCap className="h-3 w-3" /> Intern
                      </span>
                    )}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Earnings</h3>
                  <dl className="space-y-2 text-sm">
                    {earningsRows.map(([label, val]) => (
                      <div key={label} className="flex justify-between gap-4">
                        <dt className="text-gray-600">{label}</dt>
                        <dd className="font-medium text-gray-900 tabular-nums">{fmtInr(val)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Deductions</h3>
                  <dl className="space-y-2 text-sm">
                    {dedRows.length > 0 ? (
                      dedRows.map(([label, val]) => (
                        <div key={label} className="flex justify-between gap-4">
                          <dt className="text-gray-600">{label}</dt>
                          <dd className="font-medium text-gray-900 tabular-nums">{fmtInr(val)}</dd>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm">No deductions this period.</p>
                    )}
                  </dl>
                  {payslipWasIntern && Number(d.professionalTax || 0) === 0 && (
                    <p className="mt-3 text-xs text-indigo-600">
                      Professional tax (₹200) is waived for interns.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MyPayslips;
